import type { NonServicePolicy, PaperTypeRules, RuleSetConfig, SubstituteRecipientPolicy } from './types.ts';

/**
 * SOUTH CAROLINA PILOT CONFIGURATION — seed data only.
 *
 * These values come from product discovery (spec §7, §9, §13) and MUST be
 * validated by the deploying agency and counsel before production use
 * (spec OPEN-007). They are loaded into the database as rule version 1 and
 * are never referenced directly by business logic.
 */

const regularNonService: NonServicePolicy = {
  minAttempts: 3,
  minFullDaysBetween: 1,
  windows: [
    { id: 'morning', label: 'Before noon', start: '00:00', end: '12:00' },
    { id: 'afternoon', label: 'Noon – 6 PM', start: '12:00', end: '18:00' },
    { id: 'evening', label: '6 PM – 10 PM', start: '18:00', end: '22:00' },
  ],
  saturdayWildcard: { enabled: true, maxCount: 1 },
};

const substituteRecipient: SubstituteRecipientPolicy = {
  minAge: 14,
  requireName: true,
  requireRelationship: true,
  requireResidesWithDefendant: true,
  requireCompetent: true,
  requireNotAParty: true,
};

const common = {
  immediateDispositions: ['bad_address', 'outside_service_area', 'moved', 'vacant', 'deceased'],
  supervisorReviewDispositions: ['unable_to_locate'],
  serviceCutoffDaysBeforeCourt: 10,
  recipientSignature: 'optional',
} satisfies Partial<PaperTypeRules>;

export const SC_PILOT_PAPER_TYPES: PaperTypeRules[] = [
  {
    ...common,
    code: 'SUMMONS_COMPLAINT',
    name: 'Summons & Complaint',
    substituteAllowed: true,
    substituteRecipient,
    posting: 'never',
    completion: 'standard',
    nonService: regularNonService,
  },
  {
    ...common,
    code: 'SUBPOENA',
    name: 'Subpoena',
    description: 'Personal service only (RULE-009).',
    substituteAllowed: false,
    posting: 'never',
    completion: 'standard',
    nonService: regularNonService,
  },
  {
    ...common,
    code: 'RULE_TO_SHOW_CAUSE',
    name: 'Rule to Show Cause',
    substituteAllowed: true,
    substituteRecipient,
    posting: 'never',
    completion: 'standard',
    nonService: regularNonService,
  },
  {
    ...common,
    code: 'RULE_TO_VACATE',
    name: 'Rule to Vacate (Eviction)',
    description: 'Eviction timing: attempts 1–2 ≥48h apart and ≥8h different time of day; attempt 3/posting any time the day after attempt 2 (RULE-007).',
    substituteAllowed: true,
    substituteRecipient,
    posting: 'after_qualifying_attempts',
    completion: 'standard',
    nonService: null,
    eviction: { minHoursBetweenAttempt1And2: 48, minTimeOfDayDifferenceHours: 8, postingMinDaysAfterAttempt2: 1 },
  },
  {
    ...common,
    code: 'NOTICE_TO_QUIT',
    name: 'Notice to Quit',
    description: 'No substitute service (RULE-009).',
    substituteAllowed: false,
    posting: 'never',
    completion: 'standard',
    nonService: regularNonService,
  },
  {
    ...common,
    code: 'WRIT_OF_EJECTMENT',
    name: 'Writ of Ejectment',
    description: 'Serve/post, wait 24 hours, then supervisor schedules set-out (RULE-008).',
    substituteAllowed: false,
    posting: 'any_attempt',
    completion: 'standard',
    nonService: null,
    setOut: { waitHoursAfterService: 24 },
  },
  {
    ...common,
    code: 'WRIT_OF_ASSISTANCE',
    name: 'Writ of Assistance',
    description: 'Supervisor-reviewed; required action entered manually (RULE-013).',
    substituteAllowed: false,
    posting: 'never',
    completion: 'supervisor_action',
    nonService: null,
  },
  {
    ...common,
    code: 'WRIT_OF_EXECUTION',
    name: 'Writ of Execution',
    description: 'V1 documents property found and possible liquidation eligibility (RULE-014).',
    substituteAllowed: false,
    posting: 'never',
    completion: 'property_documentation',
    nonService: null,
  },
];

export const SC_PILOT_RULESET: RuleSetConfig = {
  schemaVersion: 1,
  jurisdiction: 'SC-PILOT',
  timezone: 'America/New_York',
  serviceHours: { start: '06:00', end: '22:00' },
  sundayBlocked: true,
  holidays: [
    { date: '2026-01-01', name: "New Year's Day" },
    { date: '2026-05-25', name: 'Memorial Day' },
    { date: '2026-07-04', name: 'Independence Day' },
    { date: '2026-09-07', name: 'Labor Day' },
    { date: '2026-11-26', name: 'Thanksgiving Day' },
    { date: '2026-12-25', name: 'Christmas Day' },
  ],
  overrideAttemptsQualify: false,
  gpsWarningFeet: 500,
  urgency: {
    lateReceivedAutoPriority: true,
    escalateAtDaysRemaining: 5,
    serverReminderDays: [10, 7, 3, 1],
  },
  paperTypes: SC_PILOT_PAPER_TYPES,
  fees: [
    { paperTypeCode: 'SUBPOENA', customerClass: 'attorney', amountCents: 1000 },
    { paperTypeCode: 'SUMMONS_COMPLAINT', customerClass: 'attorney', amountCents: 1500 },
    { paperTypeCode: 'RULE_TO_SHOW_CAUSE', customerClass: 'attorney', amountCents: 1500 },
    { paperTypeCode: 'RULE_TO_VACATE', customerClass: 'magistrate', amountCents: 0 },
    { paperTypeCode: 'NOTICE_TO_QUIT', customerClass: 'magistrate', amountCents: 0 },
    { paperTypeCode: 'WRIT_OF_EJECTMENT', customerClass: 'magistrate', amountCents: 0 },
  ],
};
